// components/CTASection/CTASection.js
import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Container, Grid } from "@mui/material";
import AnimatedButton from "../../../ui/AnimatedButton";
import GradientText from "../../../ui/GradientText";

const CTASection = ({ isMobile, navigate }) => {
  const stats = [
    { number: "10K+", label: "Happy Roommates", emoji: "😊" },
    { number: "95%", label: "Match Success Rate", emoji: "🎯" },
    { number: "24/7", label: "Active Community", emoji: "🌎" },
    { number: "50+", label: "Cities Worldwide", emoji: "🏙️" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        py: 10,
        background: `
          radial-gradient(circle at 50% 50%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
          linear-gradient(180deg, rgba(0, 8, 20, 0.9) 0%, rgba(0, 29, 61, 1) 100%)
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          rotate: 360,
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: "absolute",
          top: "10%",
          left: "10%",
          width: "200px",
          height: "200px",
          background: "radial-gradient(circle, rgba(0, 255, 136, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />
      
      <motion.div
        animate={{
          rotate: -360,
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: "absolute",
          bottom: "10%",
          right: "10%",
          width: "150px",
          height: "150px",
          background: "radial-gradient(circle, rgba(0, 153, 255, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Main CTA Card */}
          <motion.div
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.3 }
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "30px",
                p: { xs: 4, md: 6 },
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                mb: 8,
              }}
            >
              {/* Background Glow */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  width: "200%",
                  height: "200%",
                  background: "radial-gradient(circle, rgba(0, 255, 136, 0.1) 0%, transparent 70%)",
                  animation: "pulse 4s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 0.5 },
                    "50%": { opacity: 0.8 },
                  },
                }}
              />
              
              <Box sx={{ position: "relative", zIndex: 2 }}>
                {/* Main Heading */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <GradientText
                    variant={isMobile ? "h3" : "h2"}
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                    }}
                  >
                    Ready to Find Your Perfect Roommate? 🏠
                  </GradientText>
                </motion.div>

                {/* Subtitle */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Typography
                    variant={isMobile ? "body1" : "h6"}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      mb: 4,
                      maxWidth: "600px",
                      mx: "auto",
                      lineHeight: 1.6,
                    }}
                  >
                    Join thousands of students and young professionals who found their ideal living situation. 
                    Start your journey to a better roommate experience today!
                  </Typography>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  viewport={{ once: true }}
                  style={{
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    marginBottom: "3rem",
                  }}
                >
                  <AnimatedButton
                    onClick={() => navigate("/fmr/sign-up")}
                    variant="gradient"
                    size={isMobile ? "medium" : "large"}
                  >
                    🚀 Start Free Today
                  </AnimatedButton>
                  
                  <AnimatedButton
                    onClick={() => navigate("/fmr/posts")}
                    variant="outline"
                    size={isMobile ? "medium" : "large"}
                  >
                    🔍 Browse Roommates
                  </AnimatedButton>
                </motion.div>

                {/* Trust Indicators */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      mb: 2,
                    }}
                  >
                    Trusted by students and professionals worldwide
                  </Typography>
                  
                  {/* Security Badges */}
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" }}>
                    {["🔒 Secure", "⭐ 4.9/5 Rating", "💬 24/7 Support"].map((badge, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255,255,255,0.8)",
                            background: "rgba(255,255,255,0.1)",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                        >
                          {badge}
                        </Typography>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            variants={itemVariants}
          >
            <Grid container spacing={3} justifyContent="center">
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <motion.div
                    variants={statVariants}
                    whileHover={{ 
                      scale: 1.1,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                      }}
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.5 
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 800,
                            background: "linear-gradient(45deg, #00ff88, #0099ff)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            color: "transparent",
                            mb: 1,
                          }}
                        >
                          {stat.number}
                        </Typography>
                      </motion.div>
                      
                      <Typography
                        variant="body1"
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          fontWeight: 600,
                        }}
                      >
                        {stat.emoji} {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Final Encouragement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: "center", marginTop: "4rem" }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontStyle: "italic",
              }}
            >
              "Your future roommate is waiting. Don't keep them waiting too long! 😉"
            </Typography>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CTASection;